import { Stack, Typography } from '@mui/material';
import type { InventoryResource } from '@repo/api-gw-sdk';
import { useState } from 'react';
import { extractErrorMessage } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/errorHandling';
import { OptionMenu } from '@/components/option-menu/option-menu';
import type { OptionMenuItemType } from '@/components/option-menu/option-menu';
import { Loader } from '@/components/shared/loader';
import { useDAL } from '@/contexts/useDAL';
import { useRoles } from '@/contexts/useRoles';
import { useWorkspace } from '@/contexts/useWorkspace';
const CONTROLLED_BY_EON = 'Controlled by Eon (recommended)';
const SCAN_ONLY = 'Scan only';
const SCAN_AND_CHANGE_EVENTS = 'Scan + change events';
interface ConfigField {
  displayText: string;
  menuOptions: OptionMenuItemType[];
}
export const S3BucketBackupTypeField = ({
  item
}: {
  item: InventoryResource;
}) => {
  const dal = useDAL();
  const {
    snackbar
  } = useWorkspace();
  const {
    data: itemConfigBody,
    mutate,
    error
  } = dal.inventory.objectStoreScanMethod.useConfig(item.id);
  const {
    isAuthorized
  } = useRoles();
  const [isUpdating, setIsUpdating] = useState(false);
  const handleUpdate = async ({
    enabled,
    systemControlled
  }: {
    enabled: boolean | null;
    systemControlled: boolean;
  }) => {
    if (!itemConfigBody) {
      return;
    }
    setIsUpdating(true);
    try {
      await dal.inventory.objectStoreScanMethod.update(item.id, {
        cdcBackup: {
          ...(enabled !== null && {
            enabled
          }),
          systemControlled
        }
      });
      await mutate();
    } catch (error_0) {
      const errorMessage = await extractErrorMessage(error_0);
      snackbar.showErrorMessage(errorMessage);
    } finally {
      setIsUpdating(false);
    }
  };
  const getBackupTypeField = (): ConfigField => {
    if (error) {
      return {
        displayText: 'N/A',
        menuOptions: []
      };
    }
    if (!itemConfigBody) {
      return {
        displayText: 'Loading...',
        menuOptions: []
      };
    }
    const {
      cdcBackup
    } = itemConfigBody;
    if (!cdcBackup || typeof cdcBackup.systemControlled !== 'boolean') {
      return {
        displayText: 'N/A',
        menuOptions: []
      };
    }
    const isUserControlled = !cdcBackup.systemControlled;
    const getDisplayText = () => {
      const backupType = cdcBackup.enabled ? SCAN_AND_CHANGE_EVENTS : SCAN_ONLY;
      if (cdcBackup.systemControlled) {
        return `${backupType} (Controlled by Eon)`;
      }
      return backupType;
    };
    const isCdcEnabled = cdcBackup.enabled ?? undefined;
    const userControlledOptions: OptionMenuItemType[] = [{
      id: 'full-backup',
      text: SCAN_ONLY,
      menuItemProps: {
        selected: isUserControlled && !isCdcEnabled,
        disabled: !isAuthorized('update:inventory') || isUserControlled && !isCdcEnabled,
        onClick: () => void handleUpdate({
          enabled: false,
          systemControlled: false
        })
      }
    }, {
      id: 'event-notifications',
      text: SCAN_AND_CHANGE_EVENTS,
      menuItemProps: {
        selected: isUserControlled && isCdcEnabled,
        disabled: !isAuthorized('update:inventory') || isUserControlled && isCdcEnabled,
        onClick: () => void handleUpdate({
          enabled: true,
          systemControlled: false
        })
      }
    }];
    const menuOptions: OptionMenuItemType[] = [{
      id: 'system-controlled',
      text: CONTROLLED_BY_EON,
      menuItemProps: {
        selected: !isUserControlled,
        disabled: !isAuthorized('update:inventory') || !isUserControlled,
        onClick: () => void handleUpdate({
          enabled: null,
          systemControlled: true
        })
      }
    }, ...userControlledOptions];
    return {
      displayText: getDisplayText(),
      menuOptions
    };
  };
  const backupTypeField = getBackupTypeField();
  return <Stack direction='row' sx={{
    gap: '8px',
    alignItems: 'center'
  }}>
      <Typography>{backupTypeField.displayText}</Typography>
      {isUpdating && <Loader size='medium' />}
      {itemConfigBody && !isUpdating && backupTypeField.menuOptions.length > 0 && <OptionMenu options={backupTypeField.menuOptions} />}
    </Stack>;
};